<script>
import {mapState} from "vuex";
import {required} from "vuelidate/lib/validators";
import VFormField from "@/components/VFormField";
import VSubmitButton from "@/components/VSubmitButton";
import VSSOButton from '@/components/VSSOButton'
import VFadeTransition from "@/components/VFadeTransition";
import VImageTransition from "@/components/VImageTransition";
import env from "@/env";
import api from "@/api";
import auth from "@/auth";

const VWarningMessage = () => import("@/components/VWarningMessage");

export default {
  name: "LoginScreenForm",

  components: {
    VFormField,
    VSubmitButton,
    VSSOButton,
    VWarningMessage,
    VFadeTransition,
    VImageTransition,
  },

  data() {
    return {
      usernameValue: "",
      passwordValue: "",
      passwordType: "password",
      icon: "visibility",
      iconActive: false,
      errorMessage: "",
      providers:[],
      coexistence: false
    };
  },
  computed: {
    ...mapState({
      clientLogo: "clientLogo",
      clientName: "clientName",
      isLoading: "isLoading",
    }),
  },
  beforeMount() {
    auth.info()
    .then((sso) => {
      this.providers = sso.providers;
      this.coexistence = sso.coexistence;
    })
    .catch((error) => {
      this.$log.error(error);
    });
  },
  validations: {
    usernameValue: {
      required,
    },
    passwordValue: {
      required,
    },
  },

  methods: {
    togglePassword() {
      this.passwordType =
        this.passwordType === "password" ? "text" : "password";
      this.iconActive = !this.iconActive;
    },

    async finishAuthentication() {
      try{
        if(this.coexistence === true){
          let authorization = await auth.getInvolvesAuthToken(this.usernameValue, this.passwordValue);
          await auth.store(authorization);
        }
      }catch(error){
        this.$log.info(error);
      }

      try {
        await this.$store.dispatch("AUTHENTICATE", {
          username: this.usernameValue,
          password: this.passwordValue,
        });
        this.$ma.trackEvent({
          label: "loginSuccess",
          properties: {
            releaseVersion: this.$store.state.version.system,
          },
        });
        window.location.assign(env.redirectPath());
      } catch (error) {
        setTimeout(() => this.$store.dispatch("STOP_LOADING"), 1000);
        this.errorMessage = error.message;
      }
    },

    async authenticate() {
      this.errorMessage = "";
      this.$v.$touch();

      if (this.$v.$error || this.isLoading) {
        return;
      }

      this.$store.dispatch("START_LOADING");

      api
        .isAuthenticated()
        .then(() => window.location.assign(env.redirectPath()))
        .catch((error) => {
          this.$log.info(error);
          this.finishAuthentication();
        });
    },
  },
};
</script>

<template>
  <div class="form-container">
    <div class="login-form__logo-content">
      <VImageTransition
        :image="clientLogo"
        :text="clientName" />
    </div>
    <div class="providers-container">

      <VSSOButton
        v-for="(provider, index) in providers"
        :key="index"
        :visible="providers.length > 0"
        :link="provider.url"
        :type="provider.type">
        {{ $t('loginWith') }} {{ provider.name }}
      </VSSOButton>

      <div
        v-if="providers.length > 0"
        class="login-sso-divider-content">
        <div class="login-sso-divider-text"> {{ $t('loginOption') }} </div>
      </div>
    </div>
    <form
      class="login-form"
      @submit.prevent="authenticate">

      <div class="login-form__content">
        <div
          v-if="errorMessage"
          class="login-form__warning-content">
          <VWarningMessage
            class="login-form__warning"
            error>
            {{ errorMessage }}
          </VWarningMessage>
        </div>


        <div class="login-form__fields">
          <VFormField
            :label="$i18n.t('username')"
            :validation="$v.usernameValue"
            :validation-message="$t('requiredField')"
            v-model="usernameValue"
            name="username"
            autofocus
          />

          <VFormField
            :label="$i18n.t('password')"
            :type="passwordType"
            :icon="icon"
            :icon-action="togglePassword"
            :icon-active="iconActive"
            :validation="$v.passwordValue"
            :validation-message="$t('requiredField')"
            v-model="passwordValue"
            name="password"
          />
        </div>

        <VSubmitButton :disabled="$v.$error">
          {{ $t("login") }}
        </VSubmitButton>

        <div class="forget-password-link">
          <router-link
            :to="{ name: 'forgotPassword' }"
            class="inv-text-link">
            {{ $t("forgotPassword") }}
          </router-link>
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.form-container{
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--space-8);
  margin-bottom: var(--space-24);
}
.login-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--space-8);
  margin-bottom: var(--space-24);
}

.login-form__logo-content {
  min-height: 108px;
  max-height: 130px;
}

.login-form__content {
  max-width: 250px;
  width: 100%;
  height: 400px;
  display: flex;
  flex-direction: column;
}

.login-form__warning-content {
  min-height: 24px;
  padding-top: var(--space-8);
  padding-bottom: var(--space-8);
}

.login-form__fields {
  margin-bottom: 50px;
  max-height: 180px;
  padding-top: var(--space-8);
}

.login-form__warning + .login-form__fields {
  margin-top: var(--space-8);
}

.inv-form-field + .inv-form-field {
  margin-top: var(--space-8);
}

.forget-password-link {
  text-align: center;
  margin-top: var(--space-8);
}

.forget-password-container {
  display: flex;
  justify-content: center;
  margin-top: var(--space-8);
}
.login-sso-divider-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 30px;
  margin-top: 15px;
  margin-bottom: 15px;
  position: relative;
}
.login-sso-divider-content::before {
  content: "";
  border-bottom: 1px solid #E1E5EB;
  position: absolute;
  bottom: 10px;
  width: 100%;
}
.login-sso-divider-text {
  padding: 8px;
  background-color: rgb(255, 255, 255);
  font-family:'Segoe UI', Tahoma, Geneva, Verdana, sans-serif, 'Open Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: left;
  color: #6F7390;
  z-index: 1;
}

.providers-container {
  max-width: 250px;
}

</style>
